import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Download, User, Briefcase, GraduationCap, Building2, Layers, Globe, Award, ClipboardCheck } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionLabel from '../components/SectionLabel';
import Tag from '../components/Tag';
import cvFile from '../assets/Glauch_CV.pdf';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.45 },
};

// Role block shared by Experience and Internships (title, period, place, description).
const RoleEntry = ({ role, period, place, description }) => (
  <div className="border-b border-line px-6 py-5 last:border-0">
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
      <h3 className="text-[15px] font-semibold leading-snug text-ink-900">{role}</h3>
      <span className="flex-shrink-0 text-xs tracking-wide text-ink-400">{period}</span>
    </div>
    <div className="mt-0.5 text-sm font-medium text-navy-600">{place}</div>
    <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{description}</p>
  </div>
);

const CV = () => {
  const { t } = useTranslation();
  const experience = t('cv.experience', { returnObjects: true });
  const education = t('cv.education', { returnObjects: true });
  const internships = t('cv.internships', { returnObjects: true });
  const skills = t('cv.skills', { returnObjects: true });
  const languages = t('cv.languages', { returnObjects: true });
  const training = t('cv.training', { returnObjects: true });
  const peerReview = t('cv.peerReview', { returnObjects: true });

  return (
    <div>
      <PageHeader eyebrow="Kalle Glauch" title={t('cv.title')} lead={t('cv.lead')}>
        <a
          href={cvFile}
          download="Glauch_CV.pdf"
          className="inline-flex items-center gap-2 rounded-md bg-beige-100 px-5 py-2.5 text-sm font-semibold text-navy-900 transition-colors hover:bg-white"
        >
          <Download size={16} strokeWidth={1.9} />
          {t('cv.download')}
        </a>
      </PageHeader>

      <div className="mx-auto max-w-5xl px-6 py-14 md:px-10 md:py-16">
        {/* Profile */}
        <motion.section {...fadeUp}>
          <SectionLabel icon={User}>{t('cv.profileLabel')}</SectionLabel>
          <p className="mt-4 max-w-prose text-[15px] leading-relaxed text-ink-900">{t('cv.profile')}</p>
        </motion.section>

        {/* Experience */}
        <motion.section {...fadeUp} className="mt-14">
          <SectionLabel icon={Briefcase}>{t('cv.experienceLabel')}</SectionLabel>
          <div className="mt-5 overflow-hidden rounded-md border border-line bg-beige-50">
            {Array.isArray(experience) && experience.map((e, i) => <RoleEntry key={i} {...e} />)}
          </div>
        </motion.section>

        {/* Education */}
        <motion.section {...fadeUp} className="mt-14">
          <SectionLabel icon={GraduationCap}>{t('cv.educationLabel')}</SectionLabel>
          <div className="mt-5 overflow-hidden rounded-md border border-line bg-beige-50">
            {Array.isArray(education) &&
              education.map((e, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-0.5 border-b border-line px-6 py-4 last:border-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6"
                >
                  <div>
                    <div className="text-sm font-semibold text-ink-900">{e.degree}</div>
                    <div className="text-sm text-ink-600">{e.place}</div>
                  </div>
                  <span className="flex-shrink-0 text-xs tracking-wide text-ink-400">{e.period}</span>
                </div>
              ))}
          </div>
        </motion.section>

        {/* Internships */}
        <motion.section {...fadeUp} className="mt-14">
          <SectionLabel icon={Building2}>{t('cv.internshipsLabel')}</SectionLabel>
          <div className="mt-5 overflow-hidden rounded-md border border-line bg-beige-50">
            {Array.isArray(internships) && internships.map((e, i) => <RoleEntry key={i} {...e} />)}
          </div>
        </motion.section>

        {/* Skills & Tools */}
        <motion.section {...fadeUp} className="mt-14">
          <SectionLabel icon={Layers}>{t('cv.skillsLabel')}</SectionLabel>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {Array.isArray(skills) &&
              skills.map((s, i) => (
                <div key={i} className="rounded-md border border-line bg-beige-50 p-6">
                  <h3 className="text-sm font-semibold text-ink-900">{s.area}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{s.description}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {Array.isArray(s.tools) && s.tools.map((tool) => <Tag key={tool}>{tool}</Tag>)}
                  </div>
                </div>
              ))}
          </div>
        </motion.section>

        {/* Peer review */}
        <motion.section {...fadeUp} className="mt-14">
          <SectionLabel icon={ClipboardCheck}>{t('cv.peerReviewLabel')}</SectionLabel>
          <p className="mt-4 max-w-prose text-sm leading-relaxed text-ink-600">{t('cv.peerReviewLead')}</p>
          <div className="mt-5 overflow-hidden rounded-md border border-line bg-beige-50">
            {Array.isArray(peerReview) &&
              peerReview.map((p, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-0.5 border-b border-line px-6 py-3.5 last:border-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                >
                  <span className="text-sm font-medium text-ink-900">{p.venue}</span>
                  <span className="flex-shrink-0 text-xs font-semibold uppercase tracking-label text-navy-600">{p.kind}</span>
                </div>
              ))}
          </div>
        </motion.section>

        {/* Languages + Continuing education */}
        <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-14 md:grid-cols-2">
          <motion.section {...fadeUp}>
            <SectionLabel icon={Globe}>{t('cv.languagesLabel')}</SectionLabel>
            <div className="mt-5 overflow-hidden rounded-md border border-line bg-beige-50">
              {Array.isArray(languages) &&
                languages.map((l, i) => {
                  const [lang, level] = l.split(' — ');
                  return (
                    <div key={i} className="flex items-baseline justify-between border-b border-line px-6 py-3.5 last:border-0">
                      <span className="text-sm font-medium text-ink-900">{lang}</span>
                      <span className="text-sm text-ink-600">{level}</span>
                    </div>
                  );
                })}
            </div>
          </motion.section>

          <motion.section {...fadeUp}>
            <SectionLabel icon={Award}>{t('cv.trainingLabel')}</SectionLabel>
            <div className="mt-5 overflow-hidden rounded-md border border-line bg-beige-50">
              {Array.isArray(training) &&
                training.map((tr, i) => (
                  <div
                    key={i}
                    className="flex flex-col gap-0.5 border-b border-line px-6 py-3.5 last:border-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                  >
                    <span className="text-sm font-medium text-ink-900">{tr.title}</span>
                    <span className="text-sm text-ink-600">{tr.place}</span>
                  </div>
                ))}
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default CV;
